import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, AlertCircle, Heart, PowerOff } from 'lucide-react';
import { ApiService } from './services/api';
import { Channel } from './types';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useAuth } from './AuthContext';
import { Navigation } from './components/Navigation';
import { ThemeToggle } from './components/ThemeToggle';

const CHANNELS_PER_PAGE = 20;

const languageCodeToName: { [key: string]: string } = {
    en: 'English',
    es: 'Español',
    ko: '한국어 (Korean)',
    ja: '日本語 (Japanese)',
    fr: 'Français',
    de: 'Deutsch',
    pt: 'Português',
    ru: 'Русский',
    zh: '中文 (Chinese)',
    id: 'Bahasa Indonesia',
    hi: 'हिन्दी (Hindi)',
    tl: 'Tagalog',
    hil: 'Hiligaynon',
    ar: 'العربية (Arabic)',
    it: 'Italiano',
    lus: 'Mizo',
    my: 'Burmese (မြန်မာဘာသာ)',
    th: 'Thai (ภาษาไทย)',
    uzn: 'Uzbek (Oʻzbekcha)',
    vi: 'Vietnamese (Tiếng Việt)',
};

const getLanguageName = (code: string | null): string => {
    if (!code) return 'N/A';
    return languageCodeToName[code.toLowerCase()] || code;
};

const FavouritesPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, isAuthenticated } = useAuth();

    const [channels, setChannels] = useState<Channel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState<number>(1);
    const [totalChannels, setTotalChannels] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [deactivatingChannel, setDeactivatingChannel] = useState<number | null>(null);
    const [favouritingChannel, setFavouritingChannel] = useState<number | null>(null);

    const fetchFavourites = useCallback(async (currentPage: number, clearPrevious = false) => {
        if (!isAuthenticated) {
            setError('Please log in to view your favourites.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const apiCallParams: any = {
                page: currentPage,
                limit: CHANNELS_PER_PAGE,
            };

            const response = await ApiService.getFavouriteChannels(apiCallParams);
            if (clearPrevious || currentPage === 1) {
                setChannels(response.data);
            } else {
                setChannels(prev => [...prev, ...response.data]);
            }
            setTotalChannels(response.total);
            setHasMore(response.hasMore);
        } catch (err) {
            setError('Failed to fetch favourite channels. Please try again.');
            console.error(err);
        }
        setLoading(false);
    }, [isAuthenticated]);

    useEffect(() => {
        const isFilterOrSortChange = page === 1;
        fetchFavourites(page, isFilterOrSortChange);
    }, [fetchFavourites, page]);

    const handleLoadMore = () => {
        if (hasMore && !loading) {
            setPage(prevPage => prevPage + 1);
        }
    };

    const handleDeactivateChannel = async (channelId: number, channelName: string) => {
        if (!confirm(`Are you sure you want to deactivate "${channelName}"? This will hide it from the channel list.`)) {
            return;
        }

        setDeactivatingChannel(channelId);
        try {
            await ApiService.deactivateChannel(channelId);

            // Remove the channel from the current list
            setChannels(prev => prev.filter(channel => channel.id !== channelId));
            setTotalChannels(prev => prev - 1);

            alert(`Channel "${channelName}" has been deactivated successfully.`);
        } catch (error) {
            console.error('Error deactivating channel:', error);
            alert('Failed to deactivate channel. Please try again.');
        } finally {
            setDeactivatingChannel(null);
        }
    };

    const handleToggleFavourite = async (channelId: number, channelName: string, isCurrentlyFavourited: boolean) => {
        if (!isAuthenticated) {
            alert('Please log in to manage your favourites.');
            return;
        }

        setFavouritingChannel(channelId);
        try {
            if (isCurrentlyFavourited) {
                await ApiService.removeFromFavourites(channelId);
                // Remove the channel from the favourites list
                setChannels(prev => prev.filter(channel => channel.id !== channelId));
                setTotalChannels(prev => prev - 1);
            } else {
                await ApiService.addToFavourites(channelId);
                // Update the channel in the list to show it's now favourited
                setChannels(prev => prev.map(channel =>
                    channel.id === channelId ? { ...channel, is_favourited: true } : channel
                ));
            }
        } catch (error) {
            console.error('Error toggling favourite:', error);
            alert('Failed to update favourite status. Please try again.');
        } finally {
            setFavouritingChannel(null);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                <header className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
                    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center justify-between">
                            <Link to="/" className="flex items-center gap-3">
                                <div className="p-2 bg-purple-600 rounded-lg">
                                    <BookOpen className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">ManhwaRecap</h1>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Favourites</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </header>

                <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="text-center py-12">
                        <Heart className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Login Required</h3>
                        <p className="text-gray-600 dark:text-gray-500 mb-4">Please log in to view your favourite channels.</p>
                        <Link to="/login" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200">
                            Go to Login
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            <header className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-4 lg:gap-6">
                            <Link to="/" className="flex items-center gap-2 md:gap-3">
                                <div className="p-1.5 md:p-2 bg-purple-600 rounded-lg">
                                    <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">ManhwaRecap</h1>
                                </div>
                            </Link>
                            <Navigation />
                        </div>
                        <div className="flex items-center space-x-1 md:space-x-2 lg:space-x-4">
                            <div className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
                                {totalChannels.toLocaleString()} Channels
                            </div>
                            <ThemeToggle />
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">My Favourites</h2>
                    <Link
                        to="/channels"
                        className="mt-4 sm:mt-0 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                        Browse All Channels
                    </Link>
                </div>

                {error && (
                    <div className="text-center py-12 bg-gray-100 dark:bg-gray-800 p-6 rounded-lg shadow-xl">
                        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Error loading favourites</h3>
                        <p className="text-gray-600 dark:text-gray-500 mb-4">{error}</p>
                        <button
                            onClick={() => fetchFavourites(1, true)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {loading && channels.length === 0 && !error && <LoadingSpinner />}

                {!loading && channels.length === 0 && !error && (
                    <div className="text-center py-12">
                        <Heart className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No favourite channels yet</h3>
                        <p className="text-gray-600 dark:text-gray-500 mb-4">Start adding channels to your favourites to see them here!</p>
                        <Link
                            to="/channels"
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
                        >
                            Browse Channels
                        </Link>
                    </div>
                )}

                {channels.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {channels.map(channel => (
                            <div key={channel.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                                <Link to={`/recaps/channel/${channel.id}${channel.language ? `?language=${channel.language}` : ''}`} className="block">
                                    <div className="flex items-center gap-3 mb-3 group">
                                        <div className="flex-shrink-0">
                                            <img
                                                src={`/media/thumbnails/${channel.id}.jpg`}
                                                alt={`${channel.name} thumbnail`}
                                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-600 group-hover:border-purple-500 transition-colors"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                }}
                                            />
                                            <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center border-2 border-gray-600 hidden group-hover:border-purple-500 transition-colors">
                                                <BookOpen className="w-6 h-6 text-gray-400" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-lg font-semibold text-purple-400 truncate group-hover:text-purple-300 transition-colors" title={channel.name}>{channel.name}</h3>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleToggleFavourite(channel.id, channel.name, true);
                                                    }}
                                                    disabled={favouritingChannel === channel.id}
                                                    className="p-1.5 text-red-400 hover:text-red-300 rounded transition-colors"
                                                    title={`Remove ${channel.name} from favourites`}
                                                >
                                                    <Heart className="w-4 h-4 fill-current" />
                                                </button>
                                                {isAuthenticated && Boolean(user?.is_admin) && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleDeactivateChannel(channel.id, channel.name);
                                                        }}
                                                        disabled={deactivatingChannel === channel.id}
                                                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors border border-red-500/30 hover:border-red-400/50"
                                                        title={`Deactivate ${channel.name}`}
                                                    >
                                                        <PowerOff className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">Language: {getLanguageName(channel.language)}</p>
                                        </div>
                                    </div>
                                </Link>
                                <div className="space-y-1">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Videos: {channel.total_videos.toLocaleString()}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Subscribers: {channel.subscriber_count ? channel.subscriber_count.toLocaleString() : 'N/A'}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Last Post: {channel.last_posted ? new Date(channel.last_posted).toLocaleDateString() : 'N/A'}</p>
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-300 dark:border-gray-700">
                                        <div className="flex items-center space-x-1 bg-gray-100 dark:bg-black/40 px-2 py-1 rounded">
                                            <span className="text-xs text-gray-600 dark:text-gray-300">👁️</span>
                                            <span className="text-xs text-gray-600 dark:text-gray-300">{channel.total_views ? channel.total_views.toLocaleString() : 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center space-x-1 bg-gray-100 dark:bg-black/40 px-2 py-1 rounded">
                                            <span className="text-xs text-gray-600 dark:text-gray-300">❤️</span>
                                            <span className="text-xs text-gray-600 dark:text-gray-300">{channel.total_likes ? channel.total_likes.toLocaleString() : 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {hasMore && !loading && (
                    <div className="text-center mt-8">
                        <button
                            onClick={handleLoadMore}
                            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            Load More Favourites
                        </button>
                    </div>
                )}
                {loading && channels.length > 0 && (
                    <div className="text-center mt-8">
                        <LoadingSpinner />
                    </div>
                )}

                <p className="text-gray-600 dark:text-gray-400 mt-8">
                    <Link to="/" className="text-purple-400 hover:underline">Back to Recaps</Link>
                </p>
            </main>
        </div>
    );
};

export default FavouritesPage; 